/**
 * Public self-signup — 30-second free 14-day trial.
 * Path: /signup
 * Flow: 3 fields -> POST /api/auth/signup -> "check inbox" success screen.
 * Reseller attribution via ?ref= URL param (silent).
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { CheckCircle, EnvelopeSimple, Warning, IdentificationCard } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth/signup`;

export default function Signup() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await axios.post(API, {
        work_email: email.trim(),
        full_name: name.trim(),
        company_name: companyName.trim(),
        ref: params.get("ref") || null,
      });
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't sign you up. Try again in a minute.");
    } finally { setBusy(false); }
  };

  if (result?.duplicate_domain) {
    return <Shell>
      <Card className="p-8" data-testid="signup-dup-domain">
        <Warning size={36} weight="duotone" className="mx-auto text-amber-600 mb-2" />
        <div className="text-center font-semibold text-lg text-zinc-900">Workspace already exists</div>
        <div className="text-sm text-zinc-600 mt-2 text-center">{result.message}</div>
        <div className="mt-5 flex justify-center"><Link to="/login" className="text-sm underline">Already have an account? Sign in</Link></div>
      </Card>
    </Shell>;
  }
  if (result?.ok) {
    return <Shell>
      <Card className="p-8 text-center" data-testid="signup-success">
        <CheckCircle size={40} weight="fill" className="mx-auto text-emerald-600 mb-2" />
        <div className="text-2xl font-semibold text-zinc-900">Check your inbox</div>
        <div className="text-sm text-zinc-600 mt-2">
          We sent a one-time link to <strong>{email}</strong>. Click it to set your password — valid for 14 days.
        </div>
        <div className="mt-6 text-xs text-zinc-500 inline-flex items-center gap-1.5">
          <EnvelopeSimple size={14}/> Didn&apos;t get it in 2 min? Check spam, or
          <button onClick={() => { setResult(null); }} className="underline ml-1">try again</button>.
        </div>
      </Card>
    </Shell>;
  }

  return <Shell>
    <Card className="p-8" data-testid="signup-form-card">
      <div className="text-center">
        <div className="text-[11px] uppercase tracking-wider text-indigo-600 font-semibold">14-day free trial</div>
        <h1 className="text-2xl font-bold text-zinc-900 mt-1">Start your SaarOne workspace</h1>
        <div className="text-sm text-zinc-600 mt-1">No credit card. Full access for 14 days.</div>
      </div>
      <form onSubmit={submit} className="space-y-3 mt-6">
        <div>
          <Label className="text-xs">Work email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="signup-email" />
        </div>
        <div>
          <Label className="text-xs">Full name</Label>
          <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Priya Sharma" data-testid="signup-name" />
        </div>
        <div>
          <Label className="text-xs">Company name</Label>
          <Input required value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Acme Pvt Ltd" data-testid="signup-company" />
        </div>
        {err && (
          <div className="text-xs px-3 py-2 rounded border border-rose-300 bg-rose-50 text-rose-800" data-testid="signup-err">{err}</div>
        )}
        <Button type="submit" disabled={busy} className="w-full h-11 bg-zinc-950 hover:bg-zinc-800" data-testid="signup-submit">
          {busy ? "Creating your workspace…" : "Start free trial →"}
        </Button>
      </form>
      <div className="text-center text-xs text-zinc-500 mt-5">
        Already have an account? <Link to="/login" className="underline text-zinc-900">Sign in</Link>
      </div>
      <div className="text-center text-[10px] text-zinc-400 mt-4">
        By signing up you agree to SaarOne&apos;s <Link to="/legal/terms" className="underline" data-testid="signup-terms-link">Terms of Use</Link> &amp; <Link to="/legal/privacy" className="underline" data-testid="signup-privacy-link">Privacy Policy</Link>.
      </div>
    </Card>
  </Shell>;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">S</div>
          <div className="text-sm font-semibold text-zinc-900">SaarOne</div>
          <div className="ml-auto"><IdentificationCard size={18} className="text-zinc-400"/></div>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">{children}</div>
      </main>
      <footer className="text-center text-xs text-zinc-400 py-6">Powered by SaarOne · saarone.com</footer>
    </div>
  );
}
